import React, { Component } from "react";
import { DateTimePicker } from "@material-ui/pickers";
import dayjs from "utils/dayjs";
import { ThemeProvider } from "@material-ui/styles";
import { createMuiTheme } from "@material-ui/core";
import colors from "styles/colors.module.scss";
import SupervisorRefreshService from "services/SupervisorRefreshService";
import "./SupervisorSummary.scss";
import { uepClient } from "uepClient";
import ExcelExport from "components/ExcelExport/ExcelExport";
import WithAlert from "hoc/WithAlert";
import { getGroup } from "utils/Common";
import { UserGroup } from "constants/UserConstants";
import CollapsibleTabs from "components/CollapsibleTabs/CollapsibleTabs";
import { SupervisorSummaryTabs } from "constants/SupervisorViewConstants";
import { MdRefresh } from "react-icons/md";

class SupervisorSummary extends Component {
  constructor() {
    super();
    const initialFromDate = dayjs().local().set('hour', 0).set('minute', 0);
    const initialToDate = dayjs().local().set('hour', 23).set('minute', 59);
    this.state = {
      isLoading: true,
      fromDate: initialFromDate,
      toDate: initialToDate,
      overviewData: {
        enteredCases: 0,
        totalEnteredCases: 0,
        closedCases: 0,
        totalClosedCases: 0,
        abandonedCases: 0,
        offlineMessages: 0,
      },
      sessionsData: {
        agentsLoggedIn: 0,
        capacity: 0,
        inPlay: 0,
        sessionsAvailable: 0,
      },
      currentTab: SupervisorSummaryTabs.OVERVIEW,
    };
    this.agentGroup = getGroup();
  }
  componentDidMount() {
    this.refreshService = SupervisorRefreshService.listen().subscribe(
      this.onRefreshFired
    );
    this.autoRefreshInterval = setInterval(
      this.fetchAllDataFromApi,
      2 * 60 * 1000
    );
    this.fetchAllDataFromApi();
  }
  componentWillUnmount() {
    this.refreshService.unsubscribe();
    if (this.autoRefreshInterval) {
      clearTimeout(this.autoRefreshInterval);
    }
  }
  onRefreshFired = () => {
    this.fetchAllDataFromApi();
  };

  fetchAllDataFromApi = () => {
    this.setState({
      isLoading: true,
    });
    Promise.all([
      this.fetchOverViewData(),
      this.fetchOfflineMessages(),
      this.fetchSessionData(),
      this.fetchTotalOverViewData(),
    ])
      .then((responses) => {
        const overviewData = responses[0]; //this gets entered cases & closed cases
        const offlineMessages = responses[1];
        const sessionSummary = responses[2];
        const totalOverviewData = responses[3];
        this.setState({
          overviewData: {
            ...this.state.overviewData,
            enteredCases: overviewData.enteredCases,
            totalEnteredCases: totalOverviewData.totalEnteredCases,
            closedCases: overviewData.closedCases,
            totalClosedCases: totalOverviewData.totalClosedCases,
            offlineMessages: offlineMessages.count,
          },
          sessionsData: {
            ...this.state.sessionsData,
            agentsLoggedIn: sessionSummary.loggedInAgents,
            capacity: sessionSummary.totalCapacity,
            inPlay: sessionSummary.inPlay,
            sessionsAvailable: sessionSummary.sessionsAvailable,
          },
          isLoading: false,
        });
      })
      .catch((err) => {
        console.error(err.message);
        this.props.showAlert({
          variant: "danger",
          content: (
            <span>
              <b>Error!</b> Failed to fetch stats of Logged In agents.
            </span>
          ),
        });
      });
  };

  fetchOverViewData = () => {
    const { fromDate, toDate } = this.state;
    return new Promise((resolve, reject) => {
      uepClient()
        .get(
          `/conversation/cases?fromDateTime=${fromDate
            .utc()
            .toISOString()}&toDateTime=${toDate.utc().toISOString()}&group=${this.agentGroup
          }`
        )
        .then((res) => {
          const { data } = res;
          return resolve(data);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  };

  fetchTotalOverViewData = () => {
    //const channel ='FLEET_EDGE_AGENT';
    return new Promise((resolve, reject) => {
      uepClient()
        .get(
          `/conversation/total-cases?group=${this.agentGroup
          }`
        )
        .then((res) => {
          const { data } = res;
          return resolve(data);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  };

  fetchOfflineMessages = () => {
    const { fromDate, toDate } = this.state;
    return new Promise((resolve, reject) => {
      uepClient()
        .get(
          `conversation/offline-messages/count?fromDateTime=${dayjs(fromDate)
            .utc()
            .toISOString()}&toDateTime=${dayjs(toDate)
              .utc()
              .toISOString()}&group=${this.agentGroup}`
        )
        .then((res) => {
          return resolve(res.data);
        })
        .catch((err) => reject(err));
    });
  };

  fetchSessionData = () => {
    return new Promise((resolve, reject) => {
      uepClient()
        .get(`/stats/loggedin-agents?group=${this.agentGroup}`)
        .then((res) => resolve(res.data.summary))
        .catch((err) => reject(err));
    });
  };

  getOverviewTabData = () => {
    const {
      agentsLoggedIn,
      capacity,
      sessionsAvailable,
      inPlay,
    } = this.state.sessionsData;

    const { enteredCases, closedCases,totalEnteredCases,totalClosedCases } = this.state.overviewData;

    if (this.agentGroup === UserGroup.SERVICE) {
      return [
        { stat: "Total Entered Cases", statCount: totalEnteredCases },
        { stat: "Entered Cases", statCount: enteredCases },
        { stat: "Total Closed Cases", statCount: totalClosedCases },
        { stat: "Closed Cases", statCount: closedCases },
        { stat: "Agents Logged In", statCount: agentsLoggedIn },
        { stat: "In-play", statCount: inPlay },
      ];
    } else {
      return [
        { stat: "Total Entered Cases", statCount: totalEnteredCases },
        { stat: "Entered Cases", statCount: enteredCases },
        { stat: "Total Closed Cases", statCount: totalClosedCases },
        { stat: "Closed Cases", statCount: closedCases },
        { stat: "Agents Logged In", statCount: agentsLoggedIn },
        { stat: "Capacity", statCount: capacity },
        { stat: "In-play", statCount: inPlay },
        { stat: "Sessions Available", statCount: sessionsAvailable },
      ];
    }
  };

  getSupervisorSummaryTabs = () => {
    const { currentTab } = this.state;
    const getHighlightedClass = (tab) => {
      if (currentTab === tab) {
        return `highlighted`;
      }
      return "";
    };
    return [
      {
        value: SupervisorSummaryTabs.OVERVIEW,
        content: this.renderOverviewContent(),
        label: "Live view",
        count: null,
        color: null,
        active: currentTab === SupervisorSummaryTabs.OVERVIEW,
        className: `supervisor-summary-tab overview-tab ${getHighlightedClass(
          SupervisorSummaryTabs.OVERVIEW
        )}`,
        highlighted: currentTab === SupervisorSummaryTabs.OVERVIEW,
      },
      {
        value: SupervisorSummaryTabs.REPORT,
        content: this.renderReportTabContent(),
        label: "Report",
        count: null,
        color: null,
        active: currentTab === SupervisorSummaryTabs.REPORT,
        className: `supervisor-summary-tab report-tab ${getHighlightedClass(
          SupervisorSummaryTabs.REPORT
        )}`,
      },
    ];
  };

  onCurrentTabChange = (currentTab) => {
    this.setState({
      currentTab,
    });
  };

  renderSupervisorSummary = () => {
    return (
      <div className="supervisor-summary">
        <div className="supervisor-summary-refresh-container">
          <div className="refresh-icon-container" onClick={this.onRefreshClick}>
            <MdRefresh className="refresh-icon" />
          </div>
          <h4 className="dashboard-header">Dashboard</h4>
        </div>
        <CollapsibleTabs
          className="supervisor-summary-tabs"
          tabs={this.getSupervisorSummaryTabs()}
          onChange={this.onCurrentTabChange}
          noScroll
          data-testid="supervisor-summary-tabs"
        />
      </div>
    );
  };

  render() {
    return this.renderSupervisorSummary();
  }
  onRefreshClick = () => {
    SupervisorRefreshService.refresh();
  };

  datePickerTheme = () => {
    const color = colors.lightBlue.includes("#") ? colors.lightBlue : "#fff"; // For jest tests to pass
    return createMuiTheme({
      typography: {
        fontFamily: "Roboto",
        fontSize: 10,
      },
      palette: {
        primary: {
          main: color,
        },
      },
    });
  };

  renderDatePicker = () => {
    const { fromDate, toDate } = this.state;
    return (
      <div className="supervisor-summary-date">
        <ThemeProvider theme={this.datePickerTheme()}>
          <div className="date-field from-date">
            <div className="from-date-text date-text">{"From : "}</div>
            <DateTimePicker
              value={dayjs(fromDate).local()}
              onChange={this.onFromDateChange}
              format="hh:mm a DD-MM-YYYY"
            />
          </div>
          <div className="date-field to-date">
            <div className="to-date-text date-text">{" To : "}</div>
            <DateTimePicker
              value={dayjs(toDate).local()}
              onChange={this.onToDateChange}
              format="hh:mm a DD-MM-YYYY"
            />
          </div>
        </ThemeProvider>
      </div>
    );
  };
  onFromDateChange = (fromDate) => {
    this.setState({
      fromDate: dayjs(fromDate).local(),
    });
  };
  onToDateChange = (toDate) => {
    this.setState({
      toDate: dayjs(toDate).local(),
    });
  };

  getReportData = () => {
    const { offlineMessages } = this.state.overviewData;
    const { fromDate, toDate } = this.state;
    const fromDateISO = dayjs(fromDate).utc().toISOString();
    const toDateISO = dayjs(toDate).utc().toISOString();
    if (this.agentGroup === UserGroup.SERVICE) {
      return [
        {
          stat: "Transcript report",
          statCount: null,
          exportLink: `/report/transcript-report?fromDateTime=${fromDateISO}&toDateTime=${toDateISO}&group=${this.agentGroup}`,
          exportFileName: "Transcript-Report",
        },
        {
          stat: "Closed Cases",
          statCount: null,
          exportLink: `/report/closed-cases?group=${this.agentGroup}&fromDateTime=${fromDateISO}&toDateTime=${toDateISO}`,
          exportFileName: "Closed-Cases-Report",
        },
      ];
    } else {
      return [
        {
          stat: "Transcript report",
          statCount: null,
          exportLink: `/report/transcript-report?fromDateTime=${fromDateISO}&toDateTime=${toDateISO}&group=${this.agentGroup}`,
          exportFileName: "Transcript-Report",
        },
        {
          stat: "Closed cases",
          statCount: null,
          exportLink: `/report/closed-cases?group=${this.agentGroup}&fromDateTime=${fromDateISO}&toDateTime=${toDateISO}`,
          exportFileName: "Closed-Cases-Report",
        },
        {
          stat: "Agent status",
          exportLink: `/report/agent-status?group=${this.agentGroup}&fromDateTime=${fromDateISO}&toDateTime=${toDateISO}`,
          exportFileName: `Agents-status-Report${dayjs(fromDateISO).format(
            "DD-MM-YYYY"
          )}-${dayjs(toDateISO).format("DD-MM-YYYY")}`,
          shouldAppendDate: false,
        },
        {
          stat: "Offline Messages*",
          statCount: offlineMessages,
          exportLink: `/report/offline-message?fromDateTime=${dayjs(fromDate)
            .utc()
            .toISOString()}&toDateTime=${dayjs(toDate)
              .utc()
              .toISOString()}&group=${this.agentGroup}`,
          exportFileName: "Offline-Messages-Report",
          className: "offline-messages",
        },
      ];
    }
  };

  renderReportTabContent = () => {
    const reportTabData = this.getReportData();
    return (
      <div className="report-tab-content">
        <div className="date-header">Date</div>
        <div className="date-picker-container" data-testid="date-picker">
          {this.renderDatePicker()}
        </div>
        <div className="report-tab-listing">
          {reportTabData.map((rpData) => this.renderStatCard(rpData))}
        </div>
      </div>
    );
  };

  renderOverviewContent = () => {
    const overviewData = this.getOverviewTabData();
    return overviewData.map((ovData) => this.renderStatCard(ovData));
  };
  renderStatCard = ({
    stat,
    statCount,
    exportLink = null,
    className = "",
    exportFileName = "",
    shouldAppendDate = true,
  }) => {
    return (
      <div className={`summary-stat-card-container ${className}`} key={stat}>
        <div className={`summary-stat-card ${className}`}>
          <h6 className="stat">{stat}</h6>
          <h5 className="stat-count">{statCount}</h5>
          <div className="export-link-ctr">
            {exportLink && (
              <ExcelExport
                appendDateToFile={shouldAppendDate}
                apiUrl={exportLink}
                excelFileName={
                  exportFileName.length ? exportFileName : undefined
                } //Undefined will actually make the component use default prop value
              />
            )}
          </div>
        </div>
        {stat === "Offline Messages*" && (
          <div className="offline-msg-note">*report timing from 9pm to 9am</div>
        )}
      </div>
    );
  };
}

export default WithAlert(SupervisorSummary);
